/**
 * Dialog for lending an item from the My Items context, where there is no
 * pre-selected group. Handles three cases:
 *   - Item in 0 groups: shows an informational message.
 *   - Item in 1 group: auto-selects that group and opens LendItemDialog directly.
 *   - Item in 2+ groups: shows a group picker first, then opens LendItemDialog.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { LendItemDialog } from '@/components/LendItemDialog';
import type { ItemWithOwner } from '@/hooks/useItems';

interface ItemGroup {
  id: string;
  name: string;
}

interface LendFromMyItemsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: ItemWithOwner | null;
  currentUserId: string;
  itemGroups?: ItemGroup[];
}

/**
 *
 * @param root0
 * @param root0.open
 * @param root0.onOpenChange
 * @param root0.item
 * @param root0.currentUserId
 * @param root0.itemGroups
 */
export function LendFromMyItemsDialog({
  open,
  onOpenChange,
  item,
  currentUserId,
  itemGroups = [],
}: LendFromMyItemsDialogProps) {
  const [pickedGroupId, setPickedGroupId] = useState('');

  // If item is in exactly one group, use that group automatically; otherwise use
  // whatever the user picks in the group-picker step.
  const effectiveGroupId = itemGroups.length === 1 ? itemGroups[0].id : pickedGroupId;

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) setPickedGroupId('');
    onOpenChange(isOpen);
  };

  const { data: members } = useQuery({
    queryKey: ['group-members', effectiveGroupId],
    queryFn: async () => {
      const { data: memberships, error } = await supabase
        .from('group_memberships')
        .select('id, role, user_id, created_at')
        .eq('group_id', effectiveGroupId);
      if (error) throw error;
      if (!memberships || memberships.length === 0) return [];
      const userIds = memberships.map((m) => m.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, display_name, avatar_url')
        .in('user_id', userIds);
      if (profilesError) throw profilesError;
      const profileMap = new Map(profiles?.map((p) => [p.user_id, p]) ?? []);
      return memberships.map((m) => ({
        ...m,
        display_name: profileMap.get(m.user_id)?.display_name ?? null,
        avatar_url: profileMap.get(m.user_id)?.avatar_url ?? null,
      }));
    },
    enabled: !!effectiveGroupId,
  });

  // Case: item has no groups — informational only.
  if (itemGroups.length === 0) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to a Group First</DialogTitle>
            <DialogDescription>
              This item isn't in any groups yet. Add it to a group before lending it to a member.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  // Case: multiple groups and none picked yet — show group picker.
  if (itemGroups.length > 1 && !pickedGroupId) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select a Group</DialogTitle>
            <DialogDescription>Choose which group to lend "{item?.name}" from.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Group</Label>
              <Select value={pickedGroupId} onValueChange={setPickedGroupId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a group" />
                </SelectTrigger>
                <SelectContent>
                  {itemGroups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Case: group resolved (auto or picked) — delegate to LendItemDialog.
  return (
    <LendItemDialog
      open={open}
      onOpenChange={handleOpenChange}
      item={item}
      members={members ?? []}
      currentUserId={currentUserId}
      groupId={effectiveGroupId}
    />
  );
}
